import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { ValueMap } from '../types';

/**
 * Export value map as PDF
 */
export async function exportValueMapAsPDF(
  _valueMap: ValueMap,
  companyName?: string
): Promise<void> {
  try {
    const element = document.getElementById('value-map-export');
    if (!element) {
      console.error('Export element not found in DOM');
      throw new Error('Value map element not found. Please try again.');
    }

    console.log('Starting PDF export...');

    // Capture element as canvas
    const canvas = await html2canvas(element, {
      backgroundColor: '#ffffff',
      scale: 2, // High resolution
      logging: false,
      useCORS: true,
      allowTaint: true,
    });

    console.log('Canvas captured:', canvas.width, 'x', canvas.height);

    // Create PDF
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    const imgData = canvas.toDataURL('image/png');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);

    // Add metadata
    pdf.setProperties({
      title: `Value Map - ${companyName || 'Cloudflare'}`,
      author: 'Value Map Coach',
      subject: 'Sales Value Map',
      keywords: 'cloudflare, value map, sales',
    });

    // Download
    const filename = `value-map-${companyName || 'export'}-${new Date().toISOString().split('T')[0]}.pdf`;
    pdf.save(filename);
    
    console.log('PDF exported successfully:', filename);
  } catch (error) {
    console.error('PDF export failed:', error);
    throw new Error(`Failed to export PDF: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Export value map as PNG
 */
export async function exportValueMapAsPNG(
  _valueMap: ValueMap,
  companyName?: string
): Promise<void> {
  try {
    const element = document.getElementById('value-map-export');
    if (!element) {
      console.error('Export element not found in DOM');
      throw new Error('Value map element not found. Please try again.');
    }

    console.log('Starting PNG export...');

    // Capture element as canvas
    const canvas = await html2canvas(element, {
      backgroundColor: '#ffffff',
      scale: 3, // Very high resolution for PNG
      logging: false,
      useCORS: true,
      allowTaint: true,
    });

    console.log('Canvas captured:', canvas.width, 'x', canvas.height);

    // Convert to blob and download
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Failed to create blob from canvas'));
          return;
        }

        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `value-map-${companyName || 'export'}-${new Date().toISOString().split('T')[0]}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        console.log('PNG exported successfully');
        resolve();
      }, 'image/png');
    });
  } catch (error) {
    console.error('PNG export failed:', error);
    throw new Error(`Failed to export PNG: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}
